'use client'

import { useCallback, useState, type FormEvent } from 'react'
import { useRouter } from 'next/navigation'
import { LEARN_BASE_PATH } from '@/utilities/learnBasePath'

interface Props {
  id: string
  placeholder?: string
  initialQuery?: string
  className?: string
}

export function LearnSearchInput({
  id,
  placeholder = 'Search our guides',
  initialQuery = '',
  className,
}: Props) {
  const router = useRouter()
  const [query, setQuery] = useState(initialQuery)

  const handleSubmit = useCallback((e: FormEvent) => {
    e.preventDefault()
    const trimmed = query.trim()
    if (trimmed) {
      router.push(`${LEARN_BASE_PATH}/search?q=${encodeURIComponent(trimmed)}`)
    }
  }, [query, router])

  return (
    <form onSubmit={handleSubmit} className={className} role="search">
      <label htmlFor={id} className="sr-only">
        Search tutorials and guides
      </label>
      <div className="flex items-center gap-2.5 bg-white border border-black rounded-[10px] px-3 py-4 transition-shadow focus-within:shadow-[0_0_0_1px_black]">
        <input
          id={id}
          type="search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={placeholder}
          className="flex-1 min-w-0 body-copy-medium text-black placeholder:text-black/30 bg-transparent focus:outline-none"
        />
        {query && (
          <button
            type="button"
            onClick={() => setQuery('')}
            className="shrink-0 size-5 rounded-full bg-black/10 hover:bg-black/20 transition-colors flex items-center justify-center focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-black"
            aria-label="Clear search"
          >
            <svg
              width="10"
              height="10"
              viewBox="0 0 10 10"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M1 1L9 9M9 1L1 9"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
              />
            </svg>
          </button>
        )}
        <button
          type="submit"
          className="shrink-0 size-6 text-black focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-black rounded-[4px]"
          aria-label="Search"
        >
          <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path
              d="M21 21L16.65 16.65M19 11C19 15.4183 15.4183 19 11 19C6.58172 19 3 15.4183 3 11C3 6.58172 6.58172 3 11 3C15.4183 3 19 6.58172 19 11Z"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>
      </div>
    </form>
  )
}
